/**
 * Generic event mapper for converting CRM event objects to CalendarEvent format.
 * Supports a multi-case rendering ladder (ahoy_start_time, ahoy_end_time,
 * ahoy_start_date, ahoy_end_date) and layers meeting-specific extras only for
 * ahoy_meeting entities.
 */

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import {
  type CalendarEventGuest,
  mapInvitationsToGuests,
  type RawInvitation,
} from './invitationMapper';
import { resolveReminderSubject, resolveReminderTitle } from './reminderUtils';
import { resolveTaskTitle } from './taskUtils';

// ============================================================================
// Types
// ============================================================================

export interface CalendarEventUser {
  avatar_url?: string;
  display_name?: string;
}

export type { CalendarEventGuest } from './invitationMapper';

export interface CalendarEvent {
  id: string;
  entity_type_name: string;
  title: string;
  start_time: string;
  end_time: string;
  is_all_day: boolean;
  location?: string;
  description?: string;
  /** Org users associated with this meeting (via ahoy_meeting_to_user) */
  users?: CalendarEventUser[];
  /** Guests/attendees from calendar invitations (ahoy_invitations property) */
  guests?: CalendarEventGuest[];
  /** Join URL for the meeting, whatever the conferencing provider */
  meet_link?: string;
  /** Provider's own name for the conferencing solution ("Zoom Meeting", "Webex") */
  meet_provider?: string;
  /** Provider-hosted icon for that solution; empty for providers with no add-on */
  meet_provider_icon_url?: string;
  /** Link to open event in Google Calendar */
  html_link?: string;
  /** Google Calendar colorId ("1" through "11") */
  color?: string;
  /** When set, clicking this event opens this entity's slideout instead of the event entity */
  navigationEntityId?: string;
  navigationEntityTypeName?: string;
  /** Entity type of the reminder subject — used for client-side reminder filtering */
  subjectEntityType?: string;
  /**
   * Whether this meeting has a summary (ahoy_meeting_notes) the current user
   * can actually read. Derived from the ahoy_meeting_to_meeting_notes
   * association, requiring the target to resolve in associatedObjects — the
   * quickfetch path returns association edges without checking target
   * permission, so presence of the edge alone would leak the existence of a
   * summary the user cannot open.
   */
  hasSummary?: boolean;
  /**
   * Marks a non-persisted, preview-only event — the "ghost" task the
   * calendar renders while the composer is open with a due set. Rendered
   * dashed/translucent and non-interactive; never comes from the CRM.
   */
  isDraft?: boolean;
}

// ============================================================================
// Rendering Ladder
// ============================================================================

/**
 * Default visible duration (minutes) for a timed event that has a start time
 * but no end time. Without this the event would render as a zero-height block
 * and be effectively invisible on the time grid. 15 minutes matches the grid's
 * smallest visually-distinct slot (see WeekView: "60px per hour — enough for
 * 15/30 min events to be visually distinct").
 */
const DEFAULT_EVENT_DURATION_MINUTES = 15;

/**
 * Return an ISO timestamp *minutes* after *isoTime*. Operates on the absolute
 * instant, so it is timezone-agnostic. Falls back to the original value if the
 * input can't be parsed (keeps the prior zero-duration behavior rather than
 * throwing).
 */
function addMinutesToIso(isoTime: string, minutes: number): string {
  const date = new Date(isoTime);
  if (Number.isNaN(date.getTime())) {
    return isoTime;
  }
  return new Date(date.getTime() + minutes * 60_000).toISOString();
}

/**
 * Sentinel id for the calendar's draft "ghost" event — the live preview
 * rendered while the task composer is open with a due set. Stable so React
 * reconciles the single ghost across re-renders instead of remounting.
 */
export const DRAFT_TASK_EVENT_ID = '__draft_task__';

/**
 * Build the calendar's draft "ghost" event from the composer's current due.
 * The calendar's create affordances open the TASK composer, so the ghost is
 * an `ahoy_task` titled "New task".
 *
 * `due` is the value MemoForm emits (see combineDue): a full ISO datetime
 * (contains "T") for a timed task, or a plain `YYYY-MM-DD` for a date-only
 * one. Timed ghosts get a default 15-minute block (matching Case 5); date-only
 * ghosts are all-day, bracketed in LOCAL time as `T00:00:00`/`T23:59:59` to
 * mirror Case 7 so the all-day layout lands them on the correct day. Returns
 * null for an unparseable value.
 */
export function buildDraftTaskEvent(due: string): CalendarEvent | null {
  if (due.includes('T')) {
    const start = new Date(due);
    if (Number.isNaN(start.getTime())) {
      return null;
    }
    const startIso = start.toISOString();
    return {
      id: DRAFT_TASK_EVENT_ID,
      entity_type_name: 'ahoy_task',
      title: 'New task',
      start_time: startIso,
      end_time: addMinutesToIso(startIso, DEFAULT_EVENT_DURATION_MINUTES),
      is_all_day: false,
      isDraft: true,
    };
  }

  // Date-only: validate YYYY-MM-DD and bracket in local time (Case 7).
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) {
    return null;
  }
  return {
    id: DRAFT_TASK_EVENT_ID,
    entity_type_name: 'ahoy_task',
    title: 'New task',
    start_time: `${due}T00:00:00`,
    end_time: `${due}T23:59:59`,
    is_all_day: true,
    isDraft: true,
  };
}

/**
 * Map a single CRM object to a CalendarEvent using the multi-case rendering
 * ladder. Returns null when the entity has no renderable time/date.
 */
export function mapToCalendarEvent(
  obj: CrmObject,
  _associatedObjects: Record<string, CrmObject>
): CalendarEvent | null {
  const props = obj.properties;
  const startTime = getPropertyValue<string>(props?.ahoy_start_time);
  const endTime = getPropertyValue<string>(props?.ahoy_end_time);
  const startDate = getPropertyValue<string>(props?.ahoy_start_date);
  const endDate = getPropertyValue<string>(props?.ahoy_end_date);

  let resolvedStartTime: string;
  let resolvedEndTime: string;
  let isAllDay: boolean;

  if (startTime && endTime) {
    // Case 1: Both start_time and end_time — timed event
    resolvedStartTime = startTime;
    resolvedEndTime = endTime;
    isAllDay = false;
  } else if (startTime && !endTime && endDate) {
    // Case 2: Timed start, all-day end — coerce to timed
    resolvedStartTime = startTime;
    resolvedEndTime = endDate + 'T23:59:59';
    isAllDay = false;
  } else if (!startTime && startDate && endDate && !endTime) {
    // Case 3: All-day event (both dates, no times)
    // Resolve to local-time T00:00:00 / T23:59:59 so half-open overlap checks
    // (eventEnd > dayStart) correctly include the last day of the event.
    resolvedStartTime = startDate + 'T00:00:00';
    resolvedEndTime = endDate + 'T23:59:59';
    isAllDay = true;
  } else if (!startTime && startDate && !endDate && endTime) {
    // Case 4: All-day start, timed end — coerce to timed
    resolvedStartTime = startDate + 'T00:00:00';
    resolvedEndTime = endTime;
    isAllDay = false;
  } else if (startTime && !endTime && !endDate) {
    // Case 5: start_time alone — render with a default 15-minute duration so
    // the event is visible on the time grid instead of a zero-height block.
    resolvedStartTime = startTime;
    resolvedEndTime = addMinutesToIso(
      startTime,
      DEFAULT_EVENT_DURATION_MINUTES
    );
    isAllDay = false;
  } else if (!startTime && !startDate && endTime && !endDate) {
    // Case 6: end_time alone — zero-duration timed event at that time
    resolvedStartTime = endTime;
    resolvedEndTime = endTime;
    isAllDay = false;
  } else if (!startTime && startDate && !endDate && !endTime) {
    // Case 7: start_date alone — single-day all-day event
    // Same local-time bracketing as Case 3 so the day overlaps correctly.
    resolvedStartTime = startDate + 'T00:00:00';
    resolvedEndTime = startDate + 'T23:59:59';
    isAllDay = true;
  } else if (!startTime && !startDate && !endTime && endDate) {
    // Case 8: end_date only — single-day all-day event on that date
    resolvedStartTime = endDate + 'T00:00:00';
    resolvedEndTime = endDate + 'T23:59:59';
    isAllDay = true;
  } else {
    // Case 9: nothing renderable
    return null;
  }

  return {
    id: obj.entity_id,
    entity_type_name: obj.entity_type_name,
    title: getPropertyValue<string>(props?.ahoy_display_name) || 'Untitled',
    start_time: resolvedStartTime,
    end_time: resolvedEndTime,
    is_all_day: isAllDay,
    location: getPropertyValue<string>(props?.ahoy_location),
    description: getPropertyValue<string>(props?.ahoy_description),
  };
}

/**
 * Map a single ahoy_task CRM object to a CalendarEvent using its own due
 * properties — tasks carry `ahoy_datetime`/`ahoy_date`, not
 * `ahoy_start_time`/`ahoy_start_date`, so they fall through the
 * `mapToCalendarEvent` ladder as null and need this dedicated mapper.
 *
 * A datetime due renders as a timed event with the default duration
 * (mirrors buildDraftTaskEvent's timed branch / Case 5); a date-only due
 * renders as an all-day event bracketed in local time (mirrors
 * buildDraftTaskEvent's date-only branch / Case 7). Returns null when
 * neither due property is set. Clicking the event opens the task itself.
 */
export function mapTaskToCalendarEvent(obj: CrmObject): CalendarEvent | null {
  const props = obj.properties;
  const datetime = getPropertyValue<string>(props?.ahoy_datetime);
  const date = getPropertyValue<string>(props?.ahoy_date);
  const title = resolveTaskTitle(obj);

  if (datetime) {
    const start = new Date(datetime);
    if (!Number.isNaN(start.getTime())) {
      const startIso = start.toISOString();
      return {
        id: obj.entity_id,
        entity_type_name: 'ahoy_task',
        title,
        start_time: startIso,
        end_time: addMinutesToIso(startIso, DEFAULT_EVENT_DURATION_MINUTES),
        is_all_day: false,
        navigationEntityId: obj.entity_id,
        navigationEntityTypeName: 'ahoy_task',
      };
    }
  }

  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // Date-only due — bracketed in LOCAL time (no timezone designator on
    // the emitted timestamp), mirroring Case 3/7's all-day-event convention
    // above so the all-day layout lands the task on the correct day.
    return {
      id: obj.entity_id,
      entity_type_name: 'ahoy_task',
      title,
      start_time: `${date}T00:00:00`,
      end_time: `${date}T23:59:59`,
      is_all_day: true,
      navigationEntityId: obj.entity_id,
      navigationEntityTypeName: 'ahoy_task',
    };
  }

  return null;
}

/**
 * Map ahoy_task CRM objects to CalendarEvent format via mapTaskToCalendarEvent.
 * Skips tasks with neither ahoy_datetime nor a valid ahoy_date.
 */
export function mapCrmTasksToCalendarEvents(
  objects: CrmObject[]
): CalendarEvent[] {
  const results: CalendarEvent[] = [];
  for (const obj of objects) {
    const event = mapTaskToCalendarEvent(obj);
    if (event !== null) {
      results.push(event);
    }
  }
  return results;
}

// ============================================================================
// Meeting Overlay
// ============================================================================

/**
 * Layer meeting-specific properties onto a base CalendarEvent.
 * Only applies when obj.entity_type_name === 'ahoy_meeting'.
 */
export function applyMeetingOverlay(
  base: CalendarEvent,
  obj: CrmObject,
  associatedObjects: Record<string, CrmObject>
): CalendarEvent {
  if (obj.entity_type_name !== 'ahoy_meeting') {
    return base;
  }

  const props = obj.properties;
  const invitations = getPropertyValue<RawInvitation[]>(
    props?.ahoy_invitations
  );
  const title =
    getPropertyValue<string>(props?.ahoy_title) ||
    getPropertyValue<string>(props?.ahoy_display_name) ||
    'Untitled meeting';

  return {
    ...base,
    title,
    meet_link: getPropertyValue<string>(props?.ahoy_meeting_link),
    meet_provider: getPropertyValue<string>(props?.ahoy_meeting_provider),
    meet_provider_icon_url: getPropertyValue<string>(
      props?.ahoy_meeting_provider_icon_url
    ),
    html_link: getPropertyValue<string>(props?.ahoy_calendar_link),
    color: getPropertyValue<string>(props?.ahoy_color),
    users: mapAssociatedUsers(obj, associatedObjects),
    guests: mapInvitationsToGuests(invitations),
    hasSummary: hasReadableMeetingNotes(obj, associatedObjects),
  };
}

// ============================================================================
// Reminder Overlay
// ============================================================================

/**
 * Layer reminder-specific properties onto a base CalendarEvent.
 * Only applies when obj.entity_type_name === 'ahoy_reminder'.
 *
 * Sets navigationEntityId/navigationEntityTypeName so clicking a reminder
 * opens the subject entity (resolved via the ahoy_reminder_to_subject
 * association).
 */
function applyReminderOverlay(
  base: CalendarEvent,
  obj: CrmObject,
  associatedObjects: Record<string, CrmObject>
): CalendarEvent {
  if (obj.entity_type_name !== 'ahoy_reminder') {
    return base;
  }

  const { subject, navigationEntityId, navigationEntityTypeName } =
    resolveReminderSubject(obj, associatedObjects);

  // Shared with ReminderRow so the calendar overlay and dashboard list use
  // the same fallback chain when ahoy_display_name is empty (e.g. reminders
  // created before the ReminderDisplayNameCascade was deployed).
  const title = resolveReminderTitle(obj, subject);

  return {
    ...base,
    title,
    navigationEntityId,
    navigationEntityTypeName,
    subjectEntityType: navigationEntityTypeName,
  };
}

// ============================================================================
// Public Mapping Function
// ============================================================================

/**
 * Map CRM objects to CalendarEvent format.
 * Skips objects that are not renderable (no time/date properties).
 * Applies entity-type-specific overlays for meetings and reminders.
 */
export function mapCrmObjectsToCalendarEvents(
  objects: CrmObject[],
  associatedObjects: Record<string, CrmObject>
): CalendarEvent[] {
  const results: CalendarEvent[] = [];
  for (const obj of objects) {
    const base = mapToCalendarEvent(obj, associatedObjects);
    if (base === null) {
      continue;
    }
    const withMeeting = applyMeetingOverlay(base, obj, associatedObjects);
    results.push(applyReminderOverlay(withMeeting, obj, associatedObjects));
  }
  return results;
}

// ============================================================================
// Post-Filter
// ============================================================================

/**
 * Filter CRM objects for calendar display. Drops cancelled meetings.
 * Non-meeting entities pass through unfiltered.
 *
 * NOTE: Attendance filtering (`ahoy_meeting_to_user`) is no longer done
 * here — server-side filtering by the user's connected integrations
 * (in `useCalendarData`) subsumes it.
 */
export function filterCalendarEntities(objects: CrmObject[]): CrmObject[] {
  return objects.filter(obj => {
    if (obj.entity_type_name !== 'ahoy_meeting') {
      return true;
    }

    const status = getPropertyValue<string>(obj.properties?.ahoy_status);
    if (status === 'cancelled') {
      return false;
    }

    return true;
  });
}

// ============================================================================
// Mapping Helpers (re-exported for backwards compatibility)
// ============================================================================

/**
 * Whether the meeting has an ahoy_meeting_notes summary the user can read.
 *
 * Requires the association target to resolve in `associatedObjects` rather than
 * trusting the edge itself: the quickfetch path permission-checks only the
 * source entity, so it returns note ids the user may not be allowed to read,
 * while the /crm-objects/query/ path filters targets. Keying on the hydrated
 * object makes both paths agree and keeps an unreadable summary invisible.
 */
export function hasReadableMeetingNotes(
  meetingObj: CrmObject,
  associatedObjects: Record<string, CrmObject>
): boolean {
  return (meetingObj.associations ?? []).some(
    assoc =>
      assoc.association_type_name === 'ahoy_meeting_to_meeting_notes' &&
      Boolean(associatedObjects[assoc.target_entity_id])
  );
}

/**
 * Map associated org users to CalendarEventUser format.
 */
export function mapAssociatedUsers(
  meetingObj: CrmObject,
  associatedObjects: Record<string, CrmObject>
): CalendarEventUser[] {
  const userAssocs =
    meetingObj.associations?.filter(
      a => a.association_type_name === 'ahoy_meeting_to_user'
    ) || [];

  const users: CalendarEventUser[] = [];
  for (const assoc of userAssocs) {
    const userObj = associatedObjects[assoc.target_entity_id];
    if (!userObj) {
      continue;
    }
    users.push({
      avatar_url: getPropertyValue<string>(userObj.properties?.ahoy_avatar_url),
      display_name: getPropertyValue<string>(
        userObj.properties?.ahoy_display_name
      ),
    });
  }
  return users.sort((a, b) =>
    (a.display_name || '').localeCompare(b.display_name || '')
  );
}

// Re-exported for backwards compatibility — consumers may import directly from invitationMapper.
export { mapInvitationsToGuests };
